import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";
import { resetAll } from "./baseSlice";
import * as CertificateBatchConstants from "../../constants/CertificateBatchConstants";

const customIntl = intl();

const messageOf = (error) =>
  customIntl.formatMessage({
    id:
      CertificateBatchConstants.CERTIFICATE_BATCH_ERROR_MESSAGES[
        error?.response
      ] || "something_else_wrong",
  });

const initialState = {
  items: [],
  // The item type the items were loaded for: the store is persisted and both
  // dialogs share it, so a list must never be offered for the other type.
  itemsType: null,
  itemsRequesting: false,
  // The cohorts (audiences) the dialog's learners can be narrowed to.
  audiences: [],
  audiencesRequesting: false,
  preview: null,
  previewRequesting: false,
  previewError: null,
  creating: false,
  // Why the last create was refused (an error code), for the dialog's footer.
  createError: null,
  list: [],
  totalCount: 0,
  query: {
    pageNo: 1,
    pageSize: CertificateBatchConstants.CERTIFICATE_BATCH_PAGE_SIZE,
  },
  requesting: false,
  error: null,
};

const certificateBatchSlice = createSlice({
  name: "certificateBatches",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    certificateBatchItemsRequest: (state, action) => {
      state.itemsRequesting = true;
      state.items = [];
      state.itemsType = action.payload?.itemType ?? null;
    },
    certificateBatchItemsSuccess: (state, action) => {
      state.itemsRequesting = false;
      state.items = action.payload?.response || [];
    },
    certificateBatchItemsFail: (state, action) => {
      state.itemsRequesting = false;
      state.items = [];
      toast.error(messageOf(action.payload));
    },

    certificateBatchAudiencesRequest: (state) => {
      state.audiencesRequesting = true;
    },
    certificateBatchAudiencesSuccess: (state, action) => {
      state.audiencesRequesting = false;
      state.audiences = action.payload?.response || [];
    },
    certificateBatchAudiencesFail: (state, action) => {
      state.audiencesRequesting = false;
      state.audiences = [];
      toast.error(messageOf(action.payload));
    },

    // The dialog shows the known error codes itself; anything else is a toast.
    certificateBatchPreviewRequest: (state) => {
      state.previewRequesting = true;
      state.preview = null;
      state.previewError = null;
    },
    certificateBatchPreviewSuccess: (state, action) => {
      state.previewRequesting = false;
      state.preview = action.payload?.response || null;
    },
    certificateBatchPreviewFail: (state, action) => {
      state.previewRequesting = false;
      state.previewError = action.payload?.response || "SOMETHING_ELSE_WRONG";
      if (
        !CertificateBatchConstants.CERTIFICATE_BATCH_ERROR_MESSAGES[
          state.previewError
        ]
      ) {
        toast.error(messageOf(action.payload));
      }
    },
    // Dispatched every time the dialog opens: an old count or refusal must
    // not show. creating is left alone: a create started before a Cancel
    // can still be running, and the dialog keeps Prepare download busy until
    // it is answered (C-BUG-1). A create left "running" by a reload is
    // forgotten when the store is restored (persistStore.js).
    certificateBatchPreviewReset: (state) => {
      state.preview = null;
      state.previewRequesting = false;
      state.previewError = null;
      state.createError = null;
    },

    certificateBatchCreateRequest: (state) => {
      state.creating = true;
      state.createError = null;
    },
    certificateBatchCreateSuccess: (state) => {
      state.creating = false;
      toast.success(
        customIntl.formatMessage({ id: "certificate_batch_started" })
      );
    },
    // The dialog stays open with every choice and shows the reason in its footer.
    certificateBatchCreateFail: (state, action) => {
      state.creating = false;
      state.createError = action.payload?.response || "SOMETHING_ELSE_WRONG";
    },

    certificateBatchListRequest: (state) => {
      state.requesting = true;
      state.error = null;
    },
    certificateBatchListSuccess: (state, action) => {
      state.requesting = false;
      state.list = action.payload?.response?.list || [];
      state.totalCount = action.payload?.response?.totalCount || 0;
    },
    certificateBatchListFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      state.list = [];
      state.totalCount = 0;
      toast.error(messageOf(action.payload));
    },
    certificateBatchSetQuery: (state, action) => {
      state.query = action.payload;
    },
  },
});

export const {
  certificateBatchItemsRequest,
  certificateBatchItemsSuccess,
  certificateBatchItemsFail,
  certificateBatchAudiencesRequest,
  certificateBatchAudiencesSuccess,
  certificateBatchAudiencesFail,
  certificateBatchPreviewRequest,
  certificateBatchPreviewSuccess,
  certificateBatchPreviewFail,
  certificateBatchPreviewReset,
  certificateBatchCreateRequest,
  certificateBatchCreateSuccess,
  certificateBatchCreateFail,
  certificateBatchListRequest,
  certificateBatchListSuccess,
  certificateBatchListFail,
  certificateBatchSetQuery,
} = certificateBatchSlice.actions;
export default certificateBatchSlice.reducer;
